import { API } from 'aws-amplify';
import React from 'react';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';

import { basicReducer as reducer } from '../../../../../utils/reducers';
import { ProtectedRouteContext } from '../../../../Context';

const useBatchStatus = (Context, StatusID) => {
    const { userSettings } = React.useContext(ProtectedRouteContext);
    const initState = {
        statuses: [],
        doingAjax: false,
        validationHeaders: [],
        scholarshipDataHeaders: [],
        showCalcs: false,
        showDate: false,
        step: 1,
        chosenStatus: '',
        changeReason: '',
        reasonRequired: false,
        suppressNotifications: false,
        statusDate: moment.tz(new Date(), userSettings.ClientTimeZone),
        inError: [],
    };

    let reasonRequiredObj = {
        id: 'reasonRequired',
        message: 'Reason is required.'
    };

    let statusDateRequiredObj = {
        id: 'statusDate',
        message: 'Approved Date is required.',
    };

    let statusDateMinErrorObj = {
        id: 'statusDate',
        message: 'Approved Date must be within the last 100 years.',
    }

    let statusDateMaxErrorObj = {
        id: 'statusDate',
        message: 'Approved Date must be within the next 100 years.',
    }

    const [state, dispatch] = React.useReducer(reducer, initState);

    const retrieveStatuses = async () => {
        // setLoadingStatuses(true);
        dispatch({ type: 'doingAjax' });

        try {
            let apiFunc =
                Context === 'application'
                    ? API.get('ShipyardAPI', `/statuschange/statuses/${StatusID}`)
                    : API.get('ShipyardAPI', '/finances/invoices/statuses', {
                          queryStringParameters: {
                              CurrentStatus: StatusID,
                          },
                      });
            let headerFunc = API.post(
                'ShipyardAPI',
                Context === 'application' ? '/applications/batch_headers' : '/finances/invoices/batch_headers',
                {
                    body: {
                        action: 'update',
                    },
                }
            );

            let [response, _headers] = await Promise.all([apiFunc, headerFunc]);

            let _statuses, _newState;

            if (Context === 'application') {
                _statuses = [...response.statuses];
                _newState = {
                    validationHeaders: _headers?.validationHeaders || [],
                    scholarshipDataHeaders: _headers?.scholarshipDataHeaders || [],
                    statuses: _statuses,
                };
            } else {
                _statuses = [];

                for (const [key, value] of Object.entries(response.InvoiceStatuses)) {
                    _statuses.push({
                        StatusID: value.id,
                        Description: key,
                    });
                }

                _newState = {
                    validationHeaders: _headers,
                    statuses: _statuses,
                };
            }

            dispatch({
                type: 'update',
                newState: _newState,
            });

            toast.success('Eligible statuses retrieved.');
        } catch (e) {
            toast.error('API error.');
        } finally {
            dispatch({ type: 'ajaxDone' });
        }
    };

    const validateBatchUpdate = async (selectedRows) => {
        let id = toast.loading('Validating batch change eligibility...');
        let isValid = false;

        if (selectedRows.length === 0) {
            toast.error('Please select one or more rows to initiate the batch status update.');
            toast.dismiss(id);
            return isValid;
        }

        try {
            let { valid, message } = await API.post('ShipyardAPI', Context === "application" ? '/applications/batch_validate' : '/finances/invoices/batch_validate', {
                body: {
                    action: 'update',
                    selected: Array.from(selectedRows),
                },
            });

            isValid = valid;

            if (valid) toast.success(message);
            else {
                toast.error(message);
            }
        } catch (e) {
            toast.error('API failure.');
        } finally {
            toast.dismiss(id);
        }

        return isValid;
    };

    const sendBatchUpdate = async (newStatus, changeReason, selectedRows) => {
        dispatch({ type: 'doingAjax' });
        let id = toast.loading(`Batch updating ${Context} statuses.`);
        let updated = false;

        let body = {
            selected: Array.from(selectedRows),
            status: newStatus,
            reason: !!changeReason ? changeReason : null,
        };

        if(Context === 'application') {
            body.statusDate = state?.statusDate?.toISOString?.() || '';
        }

        try {
            let response = await API.post('ShipyardAPI', Context === 'application' ? '/applications/batch_status_update' : '/finances/invoices/batch_update', {
                body,
            });

            toast.success(response?.message || 'Successfully updated!');
            updated = true;
        } catch (e) {
            toast.error('API failure.');
        } finally {
            toast.dismiss(id);
            dispatch({ type: 'ajaxDone' });
        }

        return updated;
    };

    const handleStatusChange = async (newStatus, selected) => {
        let _newState = state;
        _newState.chosenStatus = parseInt(newStatus) || '';
        _newState.statusDate = initState.statusDate;
        _newState.changeReason = initState.changeReason;
        _newState.showDateError = false;
        _newState.showChangeReasonError = false;

        let inError = new Set(state?.inError?.map(ea => JSON.stringify(ea)));

        if (Context === 'application') {
            switch(newStatus) {
                case 3:
                    _newState.reasonRequired = true;
                    _newState.showCalcs = false;
                    _newState.showDate = false;
                    inError.add(JSON.stringify(reasonRequiredObj));
                    inError.delete(JSON.stringify(statusDateRequiredObj));
                    break;
                case 5:
                    _newState.reasonRequired = true;
                    _newState.showCalcs = false;
                    _newState.showDate = false;
                    inError.add(JSON.stringify(reasonRequiredObj));
                    inError.delete(JSON.stringify(statusDateRequiredObj));
                    break;
                case 6:
                    _newState.showDate = true;
                    _newState.reasonRequired = false;
                    _newState.showCalcs = false;
                    inError.delete(JSON.stringify(reasonRequiredObj));
                    inError.delete(JSON.stringify(statusDateRequiredObj));
                    break;
                case 8:
                    _newState.reasonRequired = false;
                    try {
                        var toastId = toast.loading('Calculating scholarship data...');
                        let scholarshipCalculations = [];
                        let response;
                        dispatch({ type: 'doingAjax' });
                        _newState.showCalcs = true;

                        if(selected?.length > 50) {
                            for(let i = 0; i < selected?.length; i+=50) {
                                toast.update(toastId, {
                                    render: `Calculating scholarship ${i/50 + 1}/${Math.ceil(selected?.length / 50)}...`,
                                });
                                response = await API.post('ShipyardAPI', '/applications/batch_scholarship_calc', {
                                    body: {
                                        selected: selected?.slice(i, i + 50)
                                    }
                                });
                                scholarshipCalculations.push(...response?.scholarshipCalculations);
                            }
    
                            scholarshipCalculations = scholarshipCalculations?.map?.(calc => {
                                let relatedAppInst = selected?.find?.(ea => ea?.ApplicationInstanceId === calc?.AppInstID) || {};
                                return {
                                    ...calc,
                                    ...relatedAppInst,
                                }
                            });
                        } else {
    
                            response = await API.post('ShipyardAPI', '/applications/batch_scholarship_calc', {
                                body: {
                                    selected
                                }
                            });
    
                            scholarshipCalculations = response?.scholarshipCalculations?.map?.(calc => {
                                let relatedAppInst = selected?.find?.(ea => ea?.ApplicationInstanceId === calc?.AppInstID) || {};
                                return {
                                    ...calc,
                                    ...relatedAppInst,
                                }
                            });
                        }
                        inError.delete(JSON.stringify(reasonRequiredObj));
                        inError.delete(JSON.stringify(statusDateRequiredObj));
        
                        toast.success('Scholarship data calculated.');
                        _newState.scholarshipData = scholarshipCalculations;
                    } catch (e) {
                        toast.error('API error.');
                    } finally {
                        toast.dismiss(toastId);
                        dispatch({ type: 'ajaxDone' });
                    }
                break;
            default:
                _newState.reasonRequired = false;
                _newState.showCalcs = false;
                _newState.showDate = false;
                inError.delete(JSON.stringify(reasonRequiredObj));
                inError.delete(JSON.stringify(statusDateRequiredObj));
            }
        } else {
            switch(newStatus) {
                case 5:
                case 8:
                    _newState.reasonRequired = true;
                    inError.add(JSON.stringify(reasonRequiredObj));
                    break;
                default:
                    _newState.reasonRequired = false;
                    inError.delete(JSON.stringify(reasonRequiredObj));
            }
        }

        _newState.inError = Array.from(inError).map(ea => JSON.parse(ea));

        dispatch({
            type: 'update',
            newState: _newState,
        });        
    };

    const handleChangeReasonChange = (reason) => {
        let inError = new Set(state?.inError?.map(ea => JSON.stringify(ea)));
        if(state?.reasonRequired && reason === '') {
            inError.add(JSON.stringify(reasonRequiredObj));
        } else {
            inError.delete(JSON.stringify(reasonRequiredObj));
        }

        dispatch({
            type: 'update',
            newState: {
                changeReason: reason,
                inError: Array.from(inError).map(ea => JSON.parse(ea)),
                showChangeReasonError: false,
            },
        });
    };

    const handleChangeReasonBlur = () => dispatch({
        type: 'update',
        newState: {
            showChangeReasonError: true,
        }
    });

    const handleDateChange = (newVal) => {
        var inError = new Set(state.inError?.map(ea => JSON.stringify(ea)));
        if (newVal === null) {
            inError.add(JSON.stringify(statusDateRequiredObj));
        } else if (newVal !== null && state.statusDate === null) {
            //Should only hit this when the field is currently in error, and is changing from blank to something not blank
            //handleDateError does not seem to be invoked in this specific scenario for some reason
            inError.delete(JSON.stringify(statusDateRequiredObj));
        }

        dispatch({
            type: 'update',
            newState: {
                statusDate: newVal,
                inError: Array.from(inError)?.map(ea => JSON.parse(ea)),
                showDateError: false,
            },
        });
    };

    const handleDateError = (err, newVal) => {
        var inError = new Set(state.inError?.map(ea => JSON.stringify(ea)));
        if (err === null && newVal === null) {
            inError.add(JSON.stringify(statusDateRequiredObj));
            inError.delete(JSON.stringify(statusDateMinErrorObj));
            inError.delete(JSON.stringify(statusDateMaxErrorObj));
        } else if (err === 'minDate') {
            inError.add(JSON.stringify(statusDateMinErrorObj));
            inError.delete(JSON.stringify(statusDateRequiredObj));
            inError.delete(JSON.stringify(statusDateMaxErrorObj));
        } else if (err === 'maxDate') {
            inError.add(JSON.stringify(statusDateMaxErrorObj));
            inError.delete(JSON.stringify(statusDateRequiredObj));
            inError.delete(JSON.stringify(statusDateMinErrorObj));
        } else if (err !== null) {
            inError.add(JSON.stringify(statusDateRequiredObj));
            inError.delete(JSON.stringify(statusDateMinErrorObj));
            inError.delete(JSON.stringify(statusDateMaxErrorObj));
        } else {
            inError.delete(JSON.stringify(statusDateRequiredObj));
            inError.delete(JSON.stringify(statusDateMinErrorObj));
            inError.delete(JSON.stringify(statusDateMaxErrorObj));
        }

        dispatch({
            type: 'update',
            newState: {
                statusDate: newVal,
                inError: Array.from(inError)?.map(ea => JSON.parse(ea)),
            },
        });
    };

    const handleDateBlur = () => dispatch({
        type: 'update',
        newState: {
            showDateError: true,
        }
    });

    const resetModal = () => {
        dispatch({
            type: 'update',
            newState: initState,
        });
    };

    const changeStep = (nextStep) => {
        dispatch({
            type: 'update',
            newState: {
                step: nextStep,
            },
        });
    };

    const handleSuppressNotifications = (suppress) => dispatch({
        type: 'update',
        newState: {
            suppressNotifications: suppress
        }
    });

    return {
        ...state,
        retrieveStatuses,
        sendBatchUpdate,
        validateBatchUpdate,
        handleStatusChange,
        handleChangeReasonChange,
        handleChangeReasonBlur,
        resetModal,
        changeStep,
        handleSuppressNotifications,
        handleDateChange,
        handleDateError,
        handleDateBlur,
    };
};

export default useBatchStatus;
